import styled from '@emotion/styled';
import React, { useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useSelector, useDispatch, IModalMode } from '~/redux';
import ReactGA from 'react-ga';

import {
  css,
  absoluteCenter,
  absoluteFill,
  fluidRange,
  transitionDefault,
  calcPerc,
  breakpoints,
  respondFrom,
  dimensions,
  respondTo,
} from '~/styles';
// import Wrapper from '~/components/common/Wrapper';

import CloseImg from '~/assets/images/close.svg';
import CloseImgInverted from '~/assets/images/close-inverted.svg';
import { closeModal } from '~/redux/actions';

import ModalBgImg from '~/assets/images/modal-background.png';
import config from '~/config';
import { useLocation } from 'react-router-dom';


const ModalContainer = styled.div<StylesProps>`
  ${absoluteFill}
  position: fixed;
  z-index: 5;

  ${transitionDefault};
  transition-duration: 0.3s;
  opacity: 0;
  visibility: hidden;
  background-color: rgba(0, 0, 0, 0.9);

  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;


  ${p => p.mode === 'fullscreen' ? css`
    background: none;

    // @media (max-width: 1023px) {
    //   top: ${dimensions.headerHeight.desktop}px;  

    //   ${respondTo(breakpoints.md, css`
    //     top: ${dimensions.headerHeight.mobile}px;  
    //   `)}
    // }
    // @media (min-width: 1024px) {
    //   top: 0;
    // }
    top: 0;
  ` : null};

  ${(p) =>
    p.show
      ? css`
          opacity: 1;
          transform: none;
          visibility: visible;
        `
      : null};
`;

const ModalOuter = styled.div<StylesProps>`
  position: relative;
  background: white;
  height: auto;
  max-height: 100%;
  width: 100%;
  @media (max-width: 1023px) {
    height: 100%;
  }

  ${respondFrom(breakpoints.lg, css`
    /* min-height: 65%; */
    max-height: 90%;
    width: ${calcPerc(1370, breakpoints.max)};
    height: auto;
  `)}


  ${p => p.mode !== 'minimal'  ? css`
    /* Safari */
    @media not all and (min-resolution:.001dpcm) { 
        @media {
          height: 100% !important;
        }
    }
  ` : null}

  ${p => p.mode === 'fullscreen' ? css`
    ${absoluteFill};
    max-height: none !important;
    width: 100% !important;
    height: auto;
  ` : null}

  ${p => p.mode === 'minimal' ? css`
    min-height: 0 !important;
    height: auto;
    @media (max-width: 1023px) {
      max-height: 225px;
    }

    ${respondFrom(breakpoints.sm, css`
      min-width: 400px;
      width: auto !important;
    `)}
  ` : null}
`;

const ModalInner = styled.div<StylesProps>`
  height: 100%;
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;
  ${fluidRange('paddingLeft', '20px', '60px')};
  ${fluidRange('paddingRight', '20px', '60px')};
    /* same as close button height */
  padding-top: ${dimensions.headerHeight.desktop - dimensions.topPanelHeight}px;
  ${fluidRange('paddingBottom', '40px', '60px')};
  line-height: 1.24;
  overflow: auto;
  ${fluidRange('fontSize', '24px', '30px')}; // default
  background-image: url(${ModalBgImg});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  /* again - check close button */
  ${respondTo(breakpoints.md, css`
    padding-top: ${dimensions.headerHeight.mobile - dimensions.topPanelHeight}px;
  `)}
`;

const CloseButton = styled.button<StylesProps>`
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  z-index: 2;

  ${fluidRange('width', '50px', '80px', breakpoints.xs, breakpoints.md)};
  /* ${fluidRange('height', '40px', '70px', breakpoints.xs, breakpoints.md)}; */
  height: ${dimensions.headerHeight.desktop - dimensions.topPanelHeight}px;

  &:after {
    content: "";
    display: block;
    ${absoluteCenter};
    background-image: url(${CloseImg});
    background-repeat: no-repeat;
    background-size: 95% 95%;

    ${transitionDefault};
    
    ${fluidRange('width', '20px', '25px', breakpoints.xs, breakpoints.md)};
    ${fluidRange('height', '20px', '25px', breakpoints.xs, breakpoints.md)};
  }

  &:hover {
    &:after {
      transform: translate(-50%, -50%) scale(1.1);
    }
  }

  /* smaller height of header */
  ${respondTo(breakpoints.md, css`
    height: ${dimensions.headerHeight.mobile - dimensions.topPanelHeight}px;
  `)}
`;

interface StylesProps {
  show: boolean;
  mode: IModalMode;
}
interface Props {}
const Modal: React.FC<Props> = () => {    
  const location = useLocation();
  const modalState = useSelector(state => state.modal);
  // const accessModuleDirectly = useSelector(state => state.accessModuleDirectly);
  const entryParams = useSelector(state => state.entryParams);

  const dispatch = useDispatch();

  const close = useCallback(
    () => {
      if (config.debug) { console.log('close modal'); }
      dispatch(closeModal());
    }, [dispatch] 
  );

  const closeOnClickOutside = useCallback(() => {
    if( modalState.mode !== 'fullscreen' ) {
      close();
    }
  }, [modalState, close]);

  /*
  const returnToReferrer = useCallback(
    () => {
       if (entryParams.refURL) {
        window.open(entryParams.refURL.toString(), '_self')
       } else {
         close();
       }
    }, []
  )
  */

  const onKeypress = useCallback(
    (e) => {
      if (e.key === 'Escape') {
          close();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [close]
  );

  useEffect(() => {
    document.addEventListener('keyup', onKeypress);
    return function cleanup() {
      document.removeEventListener('keyup', onKeypress);
    };
  }, [onKeypress]);

  // make sure to close modal when navigating
  useEffect(() => {
    close();
  }, [location.pathname, close])

  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  
  useEffect(() => {
    // disable / enable body scroll
    document.body.classList[modalState.show ? 'add' : 'remove']('noscroll');
    document.body.classList[modalState.show ? 'add' : 'remove']('modal-opened');

    // register GA modal view event
    if (modalState.show) {
      let gaLabel = modalState.content.type.name;

      if (modalState.content.type.name === 'ResourceOutputModal') {
        gaLabel += ': open resource ' 
          + modalState.content.props.config.type + ': '
          + modalState.content.props.config.title;
      }

      if (modalState.content.type.name === 'TrainingModuleModal') {
        gaLabel += ': open training module at: ' + modalState.content.props.config.url;
      }

      if (config.debug) { console.log('register', modalState, 'modal view event with label', gaLabel); }
      ReactGA.modalview(gaLabel);
    }
  }, [modalState.show])

  const stylesProps = useMemo(() => _.pick(modalState, ['mode', 'show']), [modalState]);


  return (
    <ModalContainer {...stylesProps} 
      className={`
        modal-mode--${modalState.mode}
      `}
      onClick={closeOnClickOutside}
    >
      {/* <Wrapper fullheight={true}> */}
      <ModalOuter {...stylesProps} onClick={stopPropagation}>
        <ModalInner id='modal-scroll-element' {...stylesProps}>
          {!!modalState.content && modalState.content}
        </ModalInner>
        <CloseButton {...stylesProps} onClick={close} />
      </ModalOuter>
      {/* </Wrapper> */}
    </ModalContainer>
  );
};
export default Modal;



// WEBPACK FOOTER //
// ./src/components/common/Modal.tsx